import { useFetch } from "@/hooks/useFetch";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { brl, km, fmtDate, fmtDateTime, hms } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function Routes() {
  const { data, loading, error, refetch } = useFetch("/admin/routes");
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const routes = (data?.routes || []).filter((r) => r.status === "finished");
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
      <table className="w-full text-sm" data-testid="hist-routes-table">
        <thead>
          <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3 font-semibold">Data</th>
            <th className="px-4 py-3 font-semibold">Entregador</th>
            <th className="px-4 py-3 font-semibold">KM</th>
            <th className="px-4 py-3 font-semibold">Duração</th>
            <th className="px-4 py-3 font-semibold">Auditoria</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {routes.length === 0 && <tr><td colSpan={5}><Empty /></td></tr>}
          {routes.map((r) => (
            <tr key={r.id} className="hover:bg-slate-800/40">
              <td className="px-4 py-3 text-slate-300">{fmtDate(r.date || r.start_time)}</td>
              <td className="px-4 py-3 font-medium text-slate-100">{r.driver_name}</td>
              <td className="px-4 py-3 font-mono text-slate-200">{km(r.corrected_km ?? r.reported_km ?? r.km)}</td>
              <td className="px-4 py-3 text-slate-400">{hms(r.duration_seconds)}</td>
              <td className="px-4 py-3 text-slate-300">{r.audit_status === "approved" ? "Aprovado" : "Pendente"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Payrolls() {
  const { data, loading, error, refetch } = useFetch("/admin/payroll/history");
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const history = data?.history || [];
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
      <table className="w-full text-sm" data-testid="hist-payroll-table">
        <thead>
          <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3 font-semibold">Mês</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">Total</th>
            <th className="px-4 py-3 font-semibold">Gerada em</th>
            <th className="px-4 py-3 font-semibold">Fechada em</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {history.length === 0 && <tr><td colSpan={5}><Empty /></td></tr>}
          {history.map((h) => (
            <tr key={h.id} className="hover:bg-slate-800/40">
              <td className="px-4 py-3 font-medium text-slate-100">{h.month}</td>
              <td className="px-4 py-3">
                <span className={`rounded px-2 py-0.5 text-xs font-semibold ${h.status === "closed" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                  {h.status === "closed" ? "Fechada" : "Aberta"}
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-slate-200">{brl(h.company_total)}</td>
              <td className="px-4 py-3 text-slate-400">{fmtDateTime(h.generated_at)}</td>
              <td className="px-4 py-3 text-slate-400">{h.closed_at ? fmtDateTime(h.closed_at) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Deletions() {
  const { data, loading, error, refetch } = useFetch("/admin/deletion-logs");
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const logs = data?.logs || [];
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
      <table className="w-full text-sm" data-testid="hist-deletion-table">
        <thead>
          <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3 font-semibold">Data</th>
            <th className="px-4 py-3 font-semibold">Removido</th>
            <th className="px-4 py-3 font-semibold">Perfil</th>
            <th className="px-4 py-3 font-semibold">Responsável</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {logs.length === 0 && <tr><td colSpan={4}><Empty /></td></tr>}
          {logs.map((l) => (
            <tr key={l.id} className="hover:bg-slate-800/40">
              <td className="px-4 py-3 text-slate-400">{fmtDateTime(l.deleted_at)}</td>
              <td className="px-4 py-3">
                <p className="font-medium text-slate-100">{l.target_name}</p>
                <p className="text-xs text-slate-500">{l.target_email}</p>
              </td>
              <td className="px-4 py-3 capitalize text-slate-300">{l.target_role}{l.target_vehicle_type ? ` • ${l.target_vehicle_type}` : ""}</td>
              <td className="px-4 py-3">
                <p className="text-slate-200">{l.actor_name}</p>
                <p className="text-xs text-slate-500">{l.actor_email}</p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Historico() {
  return (
    <div data-testid="historico">
      <Tabs defaultValue="rotas">
        <TabsList className="bg-slate-900">
          <TabsTrigger value="rotas" data-testid="hist-tab-rotas">Rotas finalizadas</TabsTrigger>
          <TabsTrigger value="folhas" data-testid="hist-tab-folhas">Folhas</TabsTrigger>
          <TabsTrigger value="exclusoes" data-testid="hist-tab-exclusoes">Log de exclusões</TabsTrigger>
        </TabsList>
        <TabsContent value="rotas" className="mt-4"><Routes /></TabsContent>
        <TabsContent value="folhas" className="mt-4"><Payrolls /></TabsContent>
        <TabsContent value="exclusoes" className="mt-4"><Deletions /></TabsContent>
      </Tabs>
    </div>
  );
}
